import Vue from 'vue'
import _find from 'lodash.find'
import { fillOptionAddons, updateSchemeTree } from '@/store/utils/scheme'
import { CardOptionType, CardVariant } from '@/constants/scripts'
import {
  _SCHEME__COLLAPSE_OPTION,
  _SCHEME__RESTORE_COLLAPSE_OPTION,
  _SCHEME__CURRENT_TEST,
  _SCHEME__REMOVE_VARIANT,
  _SCHEME__UPDATE,
  _SCRIPTS__REMOVE_CARD_OPTION,
  _SCRIPTS__SAVE,
  _SCRIPTS__SAVE_CARD_OPTION,
  _SCRIPTS__SAVE_CARDS,
  _SCRIPTS__SAVE_ONE,
  _SCRIPTS__SORT,
} from '../../mutations'
import { getCurrentSchemeOptionCollpsedState, setCurrentSchemeOptionCollpsedState } from '@/helpers/scheme'

import type { MutationTree } from 'vuex'
import type { ScriptsState } from './state'
import type {
  ScriptUnit,
  CardInfo,
  CardOption,
  SchemeCardInfo,
} from '@/models/scripts'

export interface SchemeCurrentTestPayload {
  cardId: string
  current: CardVariant
}

export interface SchemeCollapseOptionMutationPayload {
  cardId: string
  optionId: string
}

export const mutations: MutationTree<ScriptsState> = {
  [_SCHEME__COLLAPSE_OPTION](
    state: ScriptsState,
    payload: SchemeCollapseOptionMutationPayload,
  ): void {
    if (state.scheme.root === null) throw `Tree is empty`
    const card = state.scheme.map[payload.cardId]
    const options = card.content[card.current].options
    const option = _find(options, ['id', payload.optionId])
    if (option === undefined) {
      throw `Option with id ${payload.optionId} not found`
    }
    if (option.type === CardOptionType.Card) {
      option.collapsed = !option.collapsed
      updateSchemeTree(state)

      const schemeOptionsCollapsedData = getCurrentSchemeOptionCollpsedState()
      schemeOptionsCollapsedData[payload.cardId] = {
        ...(schemeOptionsCollapsedData[payload.cardId] || {}),
        [payload.optionId]: option.collapsed
      }
      setCurrentSchemeOptionCollpsedState(schemeOptionsCollapsedData)
    }
  },

  [_SCHEME__RESTORE_COLLAPSE_OPTION](state: ScriptsState): void {
    if (state.scheme.root === null) {
      throw `Tree is empty`
    }

    const schemeOptionsCollapsedData = getCurrentSchemeOptionCollpsedState()

    Object.keys(schemeOptionsCollapsedData).forEach(cardId => {
      const card = state.scheme.map[cardId]

      if (!card) {
        return
      }

      const options = card.content[card.current].options
      Object.keys(schemeOptionsCollapsedData[cardId]).forEach(optionId => {
        const option = options.find(o => String(o.id) === optionId)

        if (option && option.type === CardOptionType.Card) {
          option.collapsed = schemeOptionsCollapsedData[cardId][optionId]
        }
      })
    })

    updateSchemeTree(state)
  },

  [_SCHEME__UPDATE](state: ScriptsState) {
    state.scheme.update++
  },

  [_SCHEME__CURRENT_TEST](
    state: ScriptsState,
    payload: SchemeCurrentTestPayload,
  ) {
    state.scheme.map[payload.cardId].current = payload.current

    updateSchemeTree(state)
  },

  [_SCRIPTS__SAVE](state: ScriptsState, list: ScriptUnit[]): void {
    const existedScripts: Record<string, ScriptUnit> = (
      state.list || []
    ).reduce((acc, script) => {
      acc[script.id] = script
      return acc
    }, {} as Record<string, ScriptUnit>)

    list = list.map(one => {
      const instance = one.instance
      if (instance?.id !== undefined) {
        instance.conversion = 0
        if (
          instance.pass &&
          instance.target &&
          instance.pass > 0 &&
          instance.target >= 0
        ) {
          instance.conversion = instance.target / instance.pass
        }
      }
      return {
        ...existedScripts[one.id],
        ...one,
      }
    })
    state.list = list
  },

  [_SCRIPTS__SAVE_CARDS](state: ScriptsState, cards: CardInfo[]): void {
    state.scheme.root = ''
    state.scheme.update = 0
    state.scheme.map = {}
    state.scheme.numsIds.clear()
    let root = ''
    state.scheme.cards = cards.map(
      (one: CardInfo): SchemeCardInfo => {
        state.scheme.numsIds.set(one.num, one.id)
        const schemeCard = Object.assign(one as SchemeCardInfo, {
          current: 'a',
        })
        Object.keys(schemeCard.content).forEach((variant: string) => {
          if ('a' === variant || 'b' === variant) {
            schemeCard.content[variant].wide = schemeCard.content[variant]
              .options?.length
              ? schemeCard.content[variant].options.length - 1
              : 0
            schemeCard.content[variant].deep = 0
            schemeCard.content[variant].row = 0
            schemeCard.content[variant]?.options.forEach(fillOptionAddons)
          }
        })
        state.scheme.map['' + schemeCard.id] = schemeCard
        if (schemeCard.num === 1) {
          root = schemeCard.id
        }
        return schemeCard
      },
    )
    state.scheme.root = root

    updateSchemeTree(state)
  },

  [_SCRIPTS__SAVE_ONE](state: ScriptsState, payload: ScriptUnit) {
    const item = _find(state.list, script => script.id === payload.id)
    if (item === undefined) {
      state.list.push(payload)
      return
    }
    item.title = payload.title
    item.status = payload.status
    // TODO: Update after API updating #122 card
    // item.ab = payload.ab
    item.ab = !!payload.tests?.length
    // item.ver_cnt = payload.ver_cnt
    // eslint-disable-next-line
    item.ver_cnt = payload.versions?.length || 0
    const clear = ['tests', 'versions']
    clear.forEach(field => {
      Vue.set(item, field, [])
    })
    const subfields = ['instance', 'tests', 'versions']
    subfields.forEach(field => {
      if (payload[field as keyof ScriptUnit] !== undefined) {
        Vue.set(item, field, payload[field as keyof ScriptUnit])
      }
    })
  },

  [_SCRIPTS__SAVE_CARD_OPTION](
    state: ScriptsState,
    payload: { cardId: string; option: CardOption },
  ) {
    const card = state.scheme.map[payload.cardId]
    const option = payload.option
    fillOptionAddons(option)

    card.content[card.current].options.push(option)
    updateSchemeTree(state)
  },

  [_SCRIPTS__REMOVE_CARD_OPTION](state: ScriptsState, payload) {
    const card = state.scheme.map[payload.cardId]
    const content = card.content[card.current]
    content.options = content.options.filter(
      (one: CardOption) => one.id !== payload.optionId,
    )
    updateSchemeTree(state)
  },

  [_SCHEME__REMOVE_VARIANT](state: ScriptsState, payload) {
    const card = state.scheme.map[payload.cardId]
    if (payload.variant === 'a') {
      card.content.a = card.content.b
    }
    card.ab = false
    card.current = CardVariant.A
    delete card.content.b

    updateSchemeTree(state)
  },

  [_SCRIPTS__SORT](state: ScriptsState, sortBy: keyof ScriptUnit): void {
    if (state.sortColumn === sortBy) {
      state.sortAsc = !state.sortAsc
    } else {
      state.sortAsc = true
      state.sortColumn = sortBy
    }
  },
}
